import { useCallback, useMemo } from 'react';
import { Type, AlignLeft, AlignCenter, AlignRight, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TextItem, TimelineItem } from '@/types/timeline';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import { useGizmoStore } from '@/features/preview/stores/gizmo-store';
import { useTemplateActions } from '@/features/editor/stores/useTemplateActions';
import {
  PropertySection,
  PropertyRow,
  NumberInput,
  ColorPicker,
} from '../components';
import { useTranslation } from '@/features/i18n/locales';

// Available Google Fonts (subset for initial implementation)
const FONT_OPTIONS = [
  // International
  { value: 'Inter', label: 'Inter' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Bebas Neue', label: 'Bebas Neue' },

  // Korean
  { value: 'Noto Sans KR', label: '본고딕 (Noto Sans)' },
  { value: 'Nanum Gothic', label: '나눔고딕' },
  { value: 'Nanum Myeongjo', label: '나눔명조' },
  { value: 'Jua', label: '배달의민족 주아' },
  { value: 'Do Hyeon', label: '배달의민족 도현' },
  { value: 'Black Han Sans', label: '검은고딕' },
  { value: 'Dongle', label: '동글 (Dongle)' },
  { value: 'Gamja Flower', label: '감자꽃' },
  { value: 'Gowun Dodum', label: '고운 돋움' },
  { value: 'Poor Story', label: '푸른밤 (Poor Story)' },
] as const;

const FONT_WEIGHT_OPTIONS = (t: any) => [
  { value: 'normal', label: t.properties.selectWeight === '굵기 선택' ? '보통' : 'Regular' },
  { value: 'medium', label: t.properties.selectWeight === '굵기 선택' ? '중간' : 'Medium' },
  { value: 'semibold', label: t.properties.selectWeight === '굵기 선택' ? '세미볼드' : 'Semibold' },
  { value: 'bold', label: t.properties.selectWeight === '굵기 선택' ? '굵게' : 'Bold' },
] as const;

const TEXT_STROKE_MIN = 20;
const TEXT_STROKE_MAX = 30;


interface TextSectionProps {
  items: TimelineItem[];
}

/**
 * Text section - properties for text items (font, color, alignment, etc.)
 */
export function TextSection({ items }: TextSectionProps) {
  const t = useTranslation();
  const updateItem = useTimelineStore((s) => s.updateItem);
  const { syncTextStyleToAll } = useTemplateActions();

  // Gizmo store for live fontSize preview
  const setPropertiesPreviewNew = useGizmoStore((s) => s.setPropertiesPreviewNew);
  const clearPreview = useGizmoStore((s) => s.clearPreview);

  // Filter to only text items
  const textItems = useMemo(
    () => items.filter((item): item is TextItem => item.type === 'text'),
    [items]
  );

  // Memoize item IDs for stable callback dependencies
  const itemIds = useMemo(() => textItems.map((item) => item.id), [textItems]);

  // Get shared values across selected text items
  const sharedValues = useMemo(() => {
    if (textItems.length === 0) return null;

    const first = textItems[0]!;
    return {
      text: textItems.every(i => i.text === first.text) ? first.text : undefined,
      fontSize: textItems.every(i => (i.fontSize ?? 60) === (first.fontSize ?? 60)) ? (first.fontSize ?? 60) : 'mixed' as const,
      fontFamily: textItems.every(i => (i.fontFamily ?? 'Inter') === (first.fontFamily ?? 'Inter')) ? (first.fontFamily ?? 'Inter') : undefined,
      fontWeight: textItems.every(i => (i.fontWeight ?? 'normal') === (first.fontWeight ?? 'normal')) ? (first.fontWeight ?? 'normal') : undefined,
      color: textItems.every(i => i.color === first.color) ? first.color : undefined,
      strokeColor: textItems.every(i => (i.stroke?.color ?? '') === (first.stroke?.color ?? '')) ? (first.stroke?.color ?? '') : undefined,
      strokeWidth: textItems.every(i => (i.stroke?.width ?? 0) === (first.stroke?.width ?? 0)) ? (first.stroke?.width ?? 0) : 'mixed' as const,
      textAlign: textItems.every(i => (i.textAlign ?? 'center') === (first.textAlign ?? 'center')) ? (first.textAlign ?? 'center') : undefined,
      verticalAlign: textItems.every(i => (i.verticalAlign ?? 'middle') === (first.verticalAlign ?? 'middle')) ? (first.verticalAlign ?? 'middle') : undefined,
      letterSpacing: textItems.every(i => (i.letterSpacing ?? 0) === (first.letterSpacing ?? 0)) ? (first.letterSpacing ?? 0) : 'mixed' as const,
      lineHeight: textItems.every(i => (i.lineHeight ?? 1.2) === (first.lineHeight ?? 1.2)) ? (first.lineHeight ?? 1.2) : 'mixed' as const,
      backgroundColor: textItems.every(i => i.backgroundColor === first.backgroundColor) ? (first.backgroundColor ?? 'transparent') : undefined,
    };
  }, [textItems]);

  // Update all selected text items
  const updateTextItems = useCallback(
    (updates: Partial<TextItem>) => {
      textItems.forEach((item) => {
        updateItem(item.id, updates);
      });
    },
    [textItems, updateItem]
  );

  // Handlers
  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;
      textItems.forEach((item) => {
        updateItem(item.id, { text: newText, label: newText.split('\n')[0] || 'Text' });
      });
    },
    [textItems, updateItem]
  );

  // Live preview for fontSize (during drag)
  const handleFontSizeLiveChange = useCallback(
    (value: number) => {
      const previews: Record<string, { fontSize: number }> = {};
      itemIds.forEach((id) => {
        previews[id] = { fontSize: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit fontSize (on mouse up)
  const handleFontSizeChange = useCallback(
    (value: number) => {
      updateTextItems({ fontSize: value });
      queueMicrotask(() => clearPreview());
    },
    [updateTextItems, clearPreview]
  );

  const handleFontFamilyChange = useCallback(
    (value: string) => {
      updateTextItems({ fontFamily: value });
    },
    [updateTextItems]
  );

  const handleFontWeightChange = useCallback(
    (value: string) => {
      updateTextItems({ fontWeight: value as TextItem['fontWeight'] });
    },
    [updateTextItems]
  );

  // Live preview for color (during picker drag)
  const handleColorLiveChange = useCallback(
    (value: string) => {
      const previews: Record<string, { color: string }> = {};
      itemIds.forEach((id) => {
        previews[id] = { color: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit color (on picker close)
  const handleColorChange = useCallback(
    (value: string) => {
      updateTextItems({ color: value });
      queueMicrotask(() => clearPreview());
    },
    [updateTextItems, clearPreview]
  );

  const handleStrokeColorLiveChange = useCallback(
    (value: string) => {
      const previews: Record<string, { stroke: { color: string; width: number } | undefined }> = {};
      itemIds.forEach((id) => {
        const width = sharedValues?.strokeWidth === 'mixed'
          ? TEXT_STROKE_MIN
          : Math.max(TEXT_STROKE_MIN, sharedValues?.strokeWidth ?? TEXT_STROKE_MIN);
        previews[id] = {
          stroke: value ? { color: value, width } : undefined,
        };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew, sharedValues?.strokeWidth]
  );

  const handleStrokeColorChange = useCallback(
    (value: string) => {
      const nextStrokeWidth = sharedValues?.strokeWidth === 'mixed'
        ? TEXT_STROKE_MIN
        : Math.max(TEXT_STROKE_MIN, sharedValues?.strokeWidth ?? TEXT_STROKE_MIN);
      updateTextItems({
        stroke: value ? { color: value, width: nextStrokeWidth } : undefined,
      });
      queueMicrotask(() => clearPreview());
    },
    [updateTextItems, clearPreview, sharedValues?.strokeWidth]
  );

  const handleStrokeWidthLiveChange = useCallback(
    (value: number) => {
      const previews: Record<string, { stroke: { color: string; width: number } | undefined }> = {};
      itemIds.forEach((id) => {
        const color = sharedValues?.strokeColor || '#000000';
        previews[id] = {
          stroke: { color, width: Math.max(TEXT_STROKE_MIN, Math.min(TEXT_STROKE_MAX, value)) },
        };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew, sharedValues?.strokeColor]
  );

  const handleStrokeWidthChange = useCallback(
    (value: number) => {
      updateTextItems({
        stroke: {
          color: sharedValues?.strokeColor || '#000000',
          width: Math.max(TEXT_STROKE_MIN, Math.min(TEXT_STROKE_MAX, value)),
        },
      });
      queueMicrotask(() => clearPreview());
    },
    [updateTextItems, clearPreview, sharedValues?.strokeColor]
  );

  // Live preview for background color
  const handleBgColorLiveChange = useCallback(
    (value: string) => {
      const previews: Record<string, { backgroundColor: string }> = {};
      itemIds.forEach((id) => {
        previews[id] = { backgroundColor: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit background color
  const handleBgColorChange = useCallback(
    (value: string) => {
      updateTextItems({ backgroundColor: value });
      queueMicrotask(() => clearPreview());
    },
    [updateTextItems, clearPreview]
  );

  const handleTextAlignChange = useCallback(
    (value: string) => {
      updateTextItems({ textAlign: value as TextItem['textAlign'] });
    },
    [updateTextItems]
  );

  const handleVerticalAlignChange = useCallback(
    (value: string) => {
      updateTextItems({ verticalAlign: value as TextItem['verticalAlign'] });
    },
    [updateTextItems]
  );

  // Live preview for letterSpacing (during drag)
  const handleLetterSpacingLiveChange = useCallback(
    (value: number) => {
      const previews: Record<string, { letterSpacing: number }> = {};
      itemIds.forEach((id) => {
        previews[id] = { letterSpacing: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit letterSpacing (on mouse up)
  const handleLetterSpacingChange = useCallback(
    (value: number) => {
      updateTextItems({ letterSpacing: value });
      queueMicrotask(() => clearPreview());
    },
    [updateTextItems, clearPreview]
  );

  // Live preview for lineHeight (during drag)
  const handleLineHeightLiveChange = useCallback(
    (value: number) => {
      const previews: Record<string, { lineHeight: number }> = {};
      itemIds.forEach((id) => {
        previews[id] = { lineHeight: value };
      });
      setPropertiesPreviewNew(previews);
    },
    [itemIds, setPropertiesPreviewNew]
  );

  // Commit lineHeight (on mouse up)
  const handleLineHeightChange = useCallback(
    (value: number) => {
      updateTextItems({ lineHeight: value });
      queueMicrotask(() => clearPreview());
    },
    [updateTextItems, clearPreview]
  );

  // Get tracks for name-based category check
  const tracks = useTimelineStore((s) => s.tracks);

  const category = useMemo(() => {
    const first = textItems[0];
    if (!first) return 'Text';
    const track = tracks.find(t => t.id === first.trackId);
    if (track?.name.startsWith('Title')) return 'Title';
    if (track?.name === 'Caption') return 'Caption';
    return 'Text';
  }, [textItems, tracks]);

  if (textItems.length === 0 || !sharedValues) {
    return null;
  }

  return (
    <PropertySection
      key={itemIds.join(',')}
      title={category === 'Title' ? t.properties.titleLabel : t.properties.caption}
      icon={Type}
      defaultOpen={true}
    >
      {/* Text Content */}
      <PropertyRow label={t.properties.content}>
        <Textarea
          value={sharedValues.text ?? ''}
          onChange={handleTextChange}
          placeholder={sharedValues.text === undefined ? 'Mixed' : t.properties.enterText}
          className="min-h-[60px] text-xs flex-1 min-w-0"
          rows={3}
        />
      </PropertyRow>

      {/* Font Family */}
      <PropertyRow label={t.properties.font}>
        <Select
          value={sharedValues.fontFamily}
          onValueChange={handleFontFamilyChange}
        >
          <SelectTrigger className="h-7 text-xs flex-1 min-w-0">
            <SelectValue placeholder={sharedValues.fontFamily === undefined ? 'Mixed' : t.properties.selectFont} />
          </SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map((font) => (
              <SelectItem key={font.value} value={font.value} className="text-xs">
                {font.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PropertyRow>

      {/* Font Size */}
      <PropertyRow label={t.properties.size}>
        <NumberInput
          value={sharedValues.fontSize}
          onChange={handleFontSizeChange}
          onLiveChange={handleFontSizeLiveChange}
          min={8}
          max={500}
          step={1}
          unit="px"
          className="flex-1 min-w-0"
        />
      </PropertyRow>

      {/* Font Weight */}
      <PropertyRow label={t.properties.weight}>
        <Select
          value={sharedValues.fontWeight}
          onValueChange={handleFontWeightChange}
        >
          <SelectTrigger className="h-7 text-xs flex-1 min-w-0">
            <SelectValue placeholder={sharedValues.fontWeight === undefined ? 'Mixed' : t.properties.selectWeight} />
          </SelectTrigger>
          <SelectContent>
            {FONT_WEIGHT_OPTIONS(t).map((weight) => (
              <SelectItem key={weight.value} value={weight.value} className="text-xs">
                {weight.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PropertyRow>

      {/* Sync Style to All */}
      <div className="px-4 py-2">
        <Button
          onClick={() => syncTextStyleToAll()}
          className="w-full flex items-center justify-center gap-2 h-9 text-xs font-semibold bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20"
          variant="outline"
        >
          <Wand2 className="w-3.5 h-3.5" />
          {category === 'Title' ? t.properties.applyToAllTitles : t.properties.applyToAllCaptions}
        </Button>
      </div>

      {/* Text Align */}
      <PropertyRow label={t.properties.align}>
        <div className="flex gap-1">
          <Button
            variant={sharedValues.textAlign === 'left' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => handleTextAlignChange('left')}
            title="Align Left"
          >
            <AlignLeft className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant={sharedValues.textAlign === 'center' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => handleTextAlignChange('center')}
            title="Align Center"
          >
            <AlignCenter className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant={sharedValues.textAlign === 'right' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => handleTextAlignChange('right')}
            title="Align Right"
          >
            <AlignRight className="w-3.5 h-3.5" />
          </Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button
            variant={sharedValues.verticalAlign === 'top' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => handleVerticalAlignChange('top')}
            title="Align Top"
          >
            <AlignStartHorizontal className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant={sharedValues.verticalAlign === 'middle' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => handleVerticalAlignChange('middle')}
            title="Align Middle"
          >
            <AlignCenterHorizontal className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant={sharedValues.verticalAlign === 'bottom' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => handleVerticalAlignChange('bottom')}
            title="Align Bottom"
          >
            <AlignEndHorizontal className="w-3.5 h-3.5" />
          </Button>
        </div>
      </PropertyRow>

      {/* Text Color */}
      <ColorPicker
        label={t.properties.color}
        color={sharedValues.color ?? '#ffffff'}
        onChange={handleColorChange}
        onLiveChange={handleColorLiveChange}
        onReset={() => handleColorChange('#ffffff')}
        defaultColor="#ffffff"
      />

      <PropertyRow label={t.properties.strokeWidth}>
        <NumberInput
          value={sharedValues.strokeWidth}
          onChange={handleStrokeWidthChange}
          onLiveChange={handleStrokeWidthLiveChange}
          min={TEXT_STROKE_MIN}
          max={TEXT_STROKE_MAX}
          step={1}
          unit="px"
          className="flex-1 min-w-0"
        />
      </PropertyRow>

      {(sharedValues.strokeWidth === 'mixed' || (sharedValues.strokeWidth as number) > 0) && (
        <ColorPicker
          label={t.properties.strokeColor}
          color={sharedValues.strokeColor || '#000000'}
          onChange={handleStrokeColorChange}
          onLiveChange={handleStrokeColorLiveChange}
          onReset={() => handleStrokeColorChange('')}
          defaultColor=""
        />
      )}

      {/* Background Color (with Alpha) */}
      <ColorPicker
        label={t.properties.background}
        color={sharedValues.backgroundColor ?? 'transparent'}
        onChange={handleBgColorChange}
        onLiveChange={handleBgColorLiveChange}
        onReset={() => handleBgColorChange('transparent')}
        defaultColor="transparent"
        showAlpha={true}
      />

      {/* Letter Spacing */}
      <PropertyRow label={t.properties.spacing}>
        <NumberInput
          value={sharedValues.letterSpacing}
          onChange={handleLetterSpacingChange}
          onLiveChange={handleLetterSpacingLiveChange}
          min={-20}
          max={100}
          step={1}
          unit="px"
          className="flex-1 min-w-0"
        />
      </PropertyRow>

      {/* Line Height */}
      <PropertyRow label={t.properties.lineHeight}>
        <NumberInput
          value={sharedValues.lineHeight}
          onChange={handleLineHeightChange}
          onLiveChange={handleLineHeightLiveChange}
          min={0.5}
          max={3}
          step={0.1}
          className="flex-1 min-w-0"
        />
      </PropertyRow>
    </PropertySection>
  );
}
